import { Accordion, AccordionDetails, AccordionSummary, Box, IconButton, Modal, Typography } from '@mui/material';
import { useCreateIntegration, useGetNewIntegrationsFields } from 'apis/business/setting/integrations/hook';
import Button from 'components/Common/Button';
import Input from 'components/Common/Input';
import Spinner from 'components/Common/Spinner';
import { useFieldArray, useForm } from 'react-hook-form';
import Grid from '@mui/material/Grid2';
import { IoChevronDownOutline } from 'react-icons/io5';
import { MdDelete } from 'react-icons/md';
import { FaPlus } from 'react-icons/fa6';

interface Props {
  open: 'cal_com';
  handleClose: any;
}

export default function IntegrationForm({ open, handleClose }: Props) {
  const { data: formFields, isLoading } = useGetNewIntegrationsFields(open);
  const { handleSubmit, reset, control } = useForm();
  const { mutateAsync: activateIntegration, isPending } = useCreateIntegration();

  const onSubmit = async (data: any) => {
    data.integration_name = formFields?.integration_name;
    await activateIntegration(data);
    handleClose();
    reset();
  };

  const {
    fields: headerFields,
    append: headerAppend,
    remove: headerRemove
  } = useFieldArray({
    control,
    name: 'configuration.headers'
  });

  const onCloseModal = () => {
    reset();
    handleClose();
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Modal
      open={Boolean(open)}
      onClose={onCloseModal}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Box sx={style}>
        <Box component='form' onSubmit={handleSubmit(onSubmit)}>
          <Typography textTransform='capitalize' variant='h6' mb={2}>
            Activate
          </Typography>
          <Grid container spacing={3}>
            {formFields?.configuration?.map((configField: { key: string; required: boolean }, idx: number) =>
              configField.key === 'headers' ? (
                <Accordion
                  sx={{
                    boxShadow: 'none',
                    border: '1px solid #F3F3F3',
                    marginTop: 2,
                    '::before': { background: 'transparent' }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<IoChevronDownOutline style={{ fontSize: 22 }} />}
                    aria-controls='panel1-content'
                    id='panel1-header'
                  >
                    <Box display='flex' flexDirection='column'>
                      <Typography variant='h6' fontSize={16} fontWeight={600}>
                        Headers
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {headerFields.map((field: any, index: number) => (
                      <Box
                        key={field.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          mb: 3
                        }}
                      >
                        <Input
                          name={`configuration.headers[${index}].field_name`}
                          label='Field name'
                          control={control}
                          sx={{ flex: 2 }}
                          fullWidth
                        />
                        <Input
                          name={`configuration.headers[${index}].value`}
                          label='Value'
                          control={control}
                          sx={{ flex: 2 }}
                          fullWidth
                        />
                        <IconButton color='error' onClick={() => headerRemove(index)}>
                          <MdDelete />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      type='button'
                      color='secondary'
                      size='small'
                      variant='outlined'
                      startIcon={<FaPlus />}
                      onClick={() => headerAppend({ field_name: '', description: '' })}
                      sx={{ display: 'flex', margin: 'auto', marginTop: 1 }}
                    >
                      Add Field
                    </Button>
                  </AccordionDetails>
                </Accordion>
              ) : (
                <Grid size={12} marginY={2.5}>
                  <Input
                    key={idx}
                    name={`configuration.${configField?.key}`}
                    control={control}
                    label={configField?.key.replace(/_/g, ' ')}
                    fullWidth
                    required={configField.required}
                  />
                </Grid>
              )
            )}
          </Grid>
          <Button type='submit' sx={{ marginTop: 2, marginLeft: 'auto', display: 'block' }} isPending={isPending}>
            Submit
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  border: '1px solid #F3F3F3',
  boxShadow: 24,
  borderRadius: '8px',
  padding: 3,
  width: {
    xs: '90%',
    sm: 400,
    md: 500,
    lg: 500
  }
};
